/**
 * London Smoke & Cure: pre-write snapshots for `lists/{listId}.recipients[]` under
 * `lists/{listId}/recipient_snapshots/{id}`. Operator must allow this subcollection in Firebase Console rules.
 */

import { collection, doc, getDocs, limit, orderBy, query, serverTimestamp } from 'firebase/firestore';
import { db } from '../services/firebase';

/** Same collection as `LISTS_COLL` in `contactManagerUtils.js` (avoid importing the full utils graph here). */
const LISTS_COLL = 'lists';

export const RECIPIENT_SNAPSHOTS_SUBCOL = 'recipient_snapshots';

/** Leave headroom under the ~1 MiB doc limit and snapshot metadata overhead. */
export const RECIPIENT_SNAPSHOT_MAX_JSON_BYTES = 750000;

export function estimateRecipientsJsonByteLength(recipients) {
  const raw = JSON.stringify(recipients == null ? [] : recipients) || '[]';
  if (typeof Blob !== 'undefined') {
    try {
      return new Blob([raw]).size;
    } catch {
      return raw.length;
    }
  }
  return raw.length;
}

function sampleEmailsForDiagnostics(serverRecipients, max = 8) {
  const out = [];
  const arr = Array.isArray(serverRecipients) ? serverRecipients : [];
  for (const r of arr) {
    if (out.length >= max) break;
    const em = String(r?.email || r?.Email || '').trim();
    if (em) out.push(em);
  }
  return out;
}

/**
 * Fields for `transaction.set` on a new snapshot doc (includes `serverTimestamp()` placeholders).
 * @param {object[]} serverRecipients State before the write (usually current `recipients` from the transaction read).
 * @param {{ source?: string, actorUid?: string|null, actorEmail?: string|null }} meta
 */
export function buildRecipientSnapshotSetPayload(serverRecipients, meta = {}) {
  const arr = Array.isArray(serverRecipients) ? serverRecipients : [];
  const bytesEstimate = estimateRecipientsJsonByteLength(arr);
  const source = typeof meta.source === 'string' && meta.source ? meta.source : 'unknown';
  const base = {
    createdAt: serverTimestamp(),
    snapshotKind: 'pre_recipients_write',
    source,
    actorUid: meta.actorUid ?? null,
    actorEmail: meta.actorEmail ?? null,
    recipientCount: arr.length,
    bytesEstimate,
    previousRecipientsIncluded: false,
  };
  if (bytesEstimate <= RECIPIENT_SNAPSHOT_MAX_JSON_BYTES) {
    return {
      ...base,
      previousRecipients: arr,
      previousRecipientsIncluded: true,
    };
  }
  return {
    ...base,
    previousRecipientsIncluded: false,
    snapshotPayloadOmittedReason: 'payload_too_large_for_firestore_snapshot',
    sampleEmails: sampleEmailsForDiagnostics(arr, 10),
  };
}

/**
 * @param {import('firebase/firestore').Transaction} transaction
 * @param {{ skipSnapshot?: boolean, source?: string, actorUid?: string|null, actorEmail?: string|null }} meta
 */
export function appendRecipientSnapshotToTransaction(transaction, listId, serverRecipients, meta = {}) {
  if (meta.skipSnapshot) return;
  const colRef = collection(db, LISTS_COLL, listId, RECIPIENT_SNAPSHOTS_SUBCOL);
  const snapRef = doc(colRef);
  const payload = buildRecipientSnapshotSetPayload(serverRecipients, {
    source: meta.source,
    actorUid: meta.actorUid,
    actorEmail: meta.actorEmail,
  });
  transaction.set(snapRef, payload);
}

/**
 * @returns {Promise<Array<{ id: string, data: object }>>}
 */
export async function fetchRecentListRecipientSnapshots(listId, maxDocs = 25) {
  const colRef = collection(db, LISTS_COLL, listId, RECIPIENT_SNAPSHOTS_SUBCOL);
  const q = query(colRef, orderBy('createdAt', 'desc'), limit(Math.min(50, Math.max(1, maxDocs))));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, data: d.data() || {} }));
}
